import { Button, TableItem } from 'components/atoms';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { resources } from 'locales/i18n';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiMagicWand, PiWarningOctagon } from 'react-icons/pi';
import { useGetErrorCodes } from 'services/enums';
import { Column } from 'types/components/table/table.types';

enum SORT_COLUMNS {
  errorCode,
  message,
  en,
  pl,
}

const ErrorCodes = () => {
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'ErrorCodesDevTools',
    SORT_COLUMNS
  );

  const errorCodes = useGetErrorCodes();

  const errorCodeNodes = useMemo(
    () => errorCodes.data?.map(e => ({ id: e.key, message: e.value })) ?? [],
    [errorCodes.data]
  );
  const appLanguages = useMemo<string[]>(
    () => Object.keys(resources),
    [resources]
  );

  const onClickToCopy = (title: string, value: string) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const getTranslation = (lng: string, id: string) =>
    t(`error-code.${id}`, { lng: lng });

  const getMissingErrorCodes = () => {
    const missingErrorCodesByLanguage = appLanguages.map(l => ({
      [l]: errorCodeNodes
        .filter(e => getTranslation(l, e.id).includes('error-code.'))
        .map(e => e.id),
    }));

    console.log(
      'Missing error-code translations:',
      missingErrorCodesByLanguage
    );
    toast.success(`Check the console for result`);
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Languages list (${errorCodes.data?.length ?? 0})`}
          hideBackButton
          topbarContent={
            <>
              <Button
                disabled={errorCodes.isLoading}
                isClear
                onlyIcon
                onClick={getMissingErrorCodes}>
                <Button.Icon icon={<PiMagicWand />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=' '
                customI18next=' '
              />
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={errorCodes.isLoading}
        size='compact'
        // displayMode
        data={{
          nodes: errorCodeNodes,
        }}
        columns={[
          {
            label: 'Error code',
            renderCell: ({ id }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('Error code', id)}>
                <TableItem.Text text={id} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.errorCode),
          },
          {
            label: 'Message',
            renderCell: ({ message }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('Message', message)}>
                <TableItem.Text text={message} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.message),
          },
          ...appLanguages.map<Column<{ id: string }>>((l, key) => ({
            label: l.toUpperCase(),
            renderCell: ({ id }) => (
              <TableItem>
                {getTranslation(l, id).includes('error-code.') ? (
                  <TableItem.Label color='red' size='compact'>
                    <PiWarningOctagon /> NOT FOUND
                  </TableItem.Label>
                ) : (
                  <TableItem.Text text={getTranslation(l, id)} />
                )}
              </TableItem>
            ),
            resize: key !== appLanguages.length - 1,
            hide: columnsVisibility.hide(
              SORT_COLUMNS[l as keyof typeof SORT_COLUMNS]
            ),
          })),
        ]}
      />
    </TopbarPage>
  );
};

export default ErrorCodes;
